import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Star, Users, Clock, Zap, Shield, TrendingUp } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { useRef } from 'react';

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.3]);

  const handleGetApp = () => {
    trackEvent('hero_cta_click', { cta_type: 'get_app' });
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTextMe = () => {
    trackEvent('hero_cta_click', { cta_type: 'text_me' });
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Gradient Orbs */}
      <motion.div 
        className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-500/30 rounded-full blur-[120px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-[100px]"
        animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      
      {/* Background Snow Pattern */}
      <div 
        className="absolute inset-0 opacity-5 bg-cover bg-center mix-blend-overlay"
        style={{ backgroundImage: 'url(https://res.cloudinary.com/dtc6oth0i/image/upload/v1764378076/ChatGPT_Image_Nov_28_2025_08_00_36_PM_b0hops.png)' }}
      />
      
      <motion.div style={{ y, opacity }} className="relative container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          {/* Left Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6"
            >
              <Zap className="w-4 h-4 text-accent-500" fill="currentColor" />
              <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">The Snow Removal Revolution</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-[0.95]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Your driveway.<br />
              <span className="gradient-text">Cleared on demand.</span><br />
              Every. Single. Storm.
            </motion.h1>
            
            <motion.p 
              className="text-base sm:text-lg text-snow-100 mb-6 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <strong className="font-semibold text-white">Stop gambling with weather.</strong> SNOWBLOWR connects you to verified local operators. Response time can be as low as minutes. Service time: 2 hours on premium/priority, up to 12 hours on non-emergent same day, and up to 24 hours later on flexible same day service. 
              <span className="text-accent-500 font-semibold"> No contracts. No surprises. No BS.</span>
            </motion.p>

            {/* Trust Badge with User Faces */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="flex items-center gap-2.5 mb-5"
            >
              {/* User Avatar Stack with Real Photos */}
              <div className="flex -space-x-2.5">
                {[
                  'https://i.pravatar.cc/150?img=12',
                  'https://i.pravatar.cc/150?img=33',
                  'https://i.pravatar.cc/150?img=25',
                  'https://i.pravatar.cc/150?img=47',
                  'https://i.pravatar.cc/150?img=54',
                ].map((avatar, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, x: -20 }}
                    animate={{ scale: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + (i * 0.05) }}
                    className="w-9 h-9 rounded-full border-[3px] border-brand-900 shadow-lg overflow-hidden"
                  >
                    <img 
                      src={avatar} 
                      alt={`User ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                  <span className="text-white font-bold text-sm">4.9/5</span>
                </div>
                <p className="text-snow-200 text-xs font-medium">
                  <strong className="text-white">100 of your neighbours</strong> have signed up for 2025/2026
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-3 mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleGetApp}
                className="group relative overflow-hidden bg-accent-500 hover:bg-accent-600 shadow-glow-green text-base font-bold px-6 py-5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get SNOWBLOWR Now
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-accent-500 via-accent-600 to-accent-500 bg-[length:200%_100%]"
                  animate={{ backgroundPosition: ['0% 0%', '100% 0%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </Button>
              <Button 
                variant="secondary" 
                size="lg" 
                onClick={handleTextMe}
                className="bg-white text-brand-600 hover:bg-neutral-50 border-3 border-white text-base font-black px-6 py-5 shadow-lg"
              >
                Text me the app
              </Button>
            </motion.div>

            {/* App Store Badges - Smaller inline version */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mb-8"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleGetApp();
                  }}
                  className="transition-transform hover:scale-105 active:scale-95"
                  aria-label="Download on the App Store"
                >
                  <svg
                    width="135"
                    height="45"
                    viewBox="0 0 180 60"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-md"
                  >
                    <rect width="180" height="60" rx="8" fill="white" />
                    <rect width="180" height="60" rx="8" stroke="#D1D5DB" strokeWidth="1.5" fill="none" />
                    <g transform="translate(18, 15)">
                      <path d="M24.769 12.856c-.043 4.378 3.525 6.495 3.686 6.585-.031.102-.576 2.052-1.899 4.066-1.144 1.741-2.331 3.478-4.201 3.514-1.836.036-2.426-.993-4.525-.993-2.1 0-2.757.958-4.492.993-1.803.036-3.184-1.908-4.338-3.642-2.358-3.543-4.159-10.009-1.74-14.367 1.202-2.169 3.351-3.542 5.681-3.578 1.772-.036 3.445 1.192 4.527 1.192 1.08 0 3.107-1.474 5.233-1.257.891.037 3.394.36 5.001 2.713-.129.08-2.984 1.743-2.933 5.774M19.38 4.285c.957-1.158 1.602-2.768 1.426-4.369-1.378.056-3.046.918-4.034 2.076-.886 1.027-1.662 2.668-1.452 4.245 1.536.12 3.106-.78 4.06-1.952" fill="black" />
                    </g>
                    <text x="55" y="24" fill="black" fontSize="10.5" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif" fontWeight="400">Download on the</text>
                    <text x="55" y="42" fill="black" fontSize="22" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif" fontWeight="600" letterSpacing="-0.5">App Store</text>
                  </svg>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleGetApp();
                  }}
                  className="transition-transform hover:scale-105 active:scale-95"
                  aria-label="Get it on Google Play"
                >
                  <svg
                    width="135"
                    height="45"
                    viewBox="0 0 180 60"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-md"
                  >
                    <rect width="180" height="60" rx="8" fill="white" />
                    <rect width="180" height="60" rx="8" stroke="#D1D5DB" strokeWidth="1.5" fill="none" />
                    <g transform="translate(15, 10) scale(0.85)">
                      <path d="M3.609 3.661C3.22 4.05 3 4.668 3 5.438v29.125c0 .77.22 1.387.609 1.777l.063.062 16.329-16.328v-.386L3.672 3.598 3.609 3.66z" fill="#32BBFF"/>
                      <path d="M25.442 25.442l-5.442-5.442v-.386l5.442-5.442.078.043 6.442 3.662c1.84 1.043 1.84 2.751 0 3.795l-6.442 3.662-.078.043z" fill="#32BBFF"/>
                      <path d="M25.52 25.399L20 19.879 3.609 36.27c.609.642 1.609.72 2.751.078l19.16-10.87" fill="#32BBFF"/>
                      <path d="M25.52 14.36L6.36 3.49c-1.142-.642-2.142-.564-2.751.078L20 19.879l5.52-5.52z" fill="#32BBFF"/>
                      <defs>
                        <linearGradient id="playGrad1" x1="21.8" y1="8.71" x2="5.02" y2="25.49" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#FFD900"/>
                          <stop offset="1" stopColor="#FFB300"/>
                        </linearGradient>
                        <linearGradient id="playGrad2" x1="33.83" y1="20.25" x2="9.64" y2="20.25" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#00D7FF"/>
                          <stop offset="1" stopColor="#00A3FF"/>
                        </linearGradient>
                        <linearGradient id="playGrad3" x1="24.83" y1="28.68" x2="2.07" y2="51.44" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#00F076"/>
                          <stop offset="1" stopColor="#00C96B"/>
                        </linearGradient>
                        <linearGradient id="playGrad4" x1="7.3" y1="0.18" x2="17.46" y2="10.34" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#FF3E00"/>
                          <stop offset="1" stopColor="#E50000"/>
                        </linearGradient>
                      </defs>
                      <path d="M25.52 14.36L6.36 3.49c-1.142-.642-2.142-.564-2.751.078L20 19.879l5.52-5.52z" fill="url(#playGrad1)"/>
                      <path d="M25.442 25.442l-5.442-5.442v-.386l5.442-5.442.078.043 6.442 3.662c1.84 1.043 1.84 2.751 0 3.795l-6.442 3.662-.078.043z" fill="url(#playGrad2)"/>
                      <path d="M25.52 25.399L20 19.879 3.609 36.27c.609.642 1.609.72 2.751.078l19.16-10.87" fill="url(#playGrad3)"/>
                      <path d="M3.609 3.661C3.22 4.05 3 4.668 3 5.438v29.125c0 .77.22 1.387.609 1.777l.063.062 16.329-16.328v-.386L3.672 3.598 3.609 3.66z" fill="url(#playGrad4)"/>
                    </g>
                    <text x="60" y="24" fill="black" fontSize="10.5" fontFamily="'Roboto', Arial, sans-serif" fontWeight="400" letterSpacing="0.5">GET IT ON</text>
                    <text x="60" y="42" fill="black" fontSize="21" fontFamily="'Roboto', Arial, sans-serif" fontWeight="500">Google Play</text>
                  </svg>
                </a>
              </div>
            </motion.div>
            
            {/* Trust Indicators */}
            <motion.div 
              className="grid grid-cols-3 gap-4 pt-6 border-t border-white/20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-1 mb-1">
                  <Star className="h-4 w-4 text-accent-500" fill="currentColor" />
                  <span className="text-xl font-black text-white">4.9</span>
                </div>
                <p className="text-xs text-snow-200 font-medium">App Store Rating</p>
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <Users className="h-4 w-4 text-accent-500" />
                  <span className="text-xl font-black text-white">100+</span>
                </div>
                <p className="text-xs text-snow-200 font-medium">Neighbours Signed Up</p>
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-accent-500" />
                  <span className="text-xl font-black text-white">2hr</span>
                </div>
                <p className="text-xs text-snow-200 font-medium">Avg. Service Time</p>
              </div>
            </motion.div>
          </div>

          {/* Right Column - iPhone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative lg:justify-self-center lg:-ml-24 z-0"
          >
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <img 
                src="https://res.cloudinary.com/dtc6oth0i/image/upload/v1764375171/ChatGPT_Image_Nov_28__2025__07_11_44_PM-removebg-preview_hqde1r.png" 
                alt="SNOWBLOWR app interface" 
                className="w-full max-w-md mx-auto drop-shadow-2xl"
              />
            </motion.div>
            
            {/* Floating UI Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="absolute top-[8%] -left-20 z-10 hidden lg:block"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
                className="bg-white text-neutral-900 rounded-xl px-5 py-3 shadow-[0_10px_40px_rgba(0,0,0,0.25)] border-2 border-neutral-200"
              >
                <p className="text-3xl font-black text-brand-500 mb-0.5">$42</p>
                <p className="text-[10px] font-bold text-neutral-600 uppercase tracking-wide">Your Price</p>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="absolute bottom-[10%] -right-10 z-10 hidden lg:block"
            >
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 0.5 }}
                className="bg-accent-500 text-white rounded-xl px-5 py-3 shadow-[0_10px_40px_rgba(32,193,90,0.3)] border-2 border-accent-600"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                  <span className="text-xs font-black uppercase tracking-wide">Live Now</span>
                </div>
                <p className="text-2xl font-black mb-0.5">8 Operators</p>
                <p className="text-[10px] font-bold opacity-90">In Your Area</p>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="absolute bottom-[15%] -left-24 z-10 hidden lg:block"
            >
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="bg-white text-neutral-900 rounded-xl px-4 py-2.5 shadow-[0_10px_40px_rgba(0,0,0,0.25)] border-2 border-neutral-200 flex items-center gap-2.5"
              >
                <div className="w-9 h-9 rounded-lg bg-brand-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-neutral-500 mb-0.5">Verified</p>
                  <p className="text-xs font-black text-brand-500">Licensed & Insured</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Additional Floating Badges */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="absolute top-[35%] -right-16 z-10 hidden xl:block"
            >
              <motion.div
                animate={{ rotate: [0, 5, 0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-xl px-4 py-2 shadow-[0_10px_40px_rgba(251,191,36,0.3)] border-2 border-yellow-300"
              >
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                  <span className="text-sm font-black">4.9</span>
                </div>
                <p className="text-[9px] font-bold opacity-90">App Rating</p>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.6 }}
              className="absolute top-[60%] -left-20 z-10 hidden xl:block"
            >
              <motion.div
                animate={{ y: [0, -8, 0], rotate: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 1 }}
                className="bg-white text-neutral-900 rounded-xl px-3 py-2 shadow-[0_10px_40px_rgba(0,0,0,0.2)] border-2 border-neutral-200"
              >
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3 text-brand-500" />
                  <span className="text-xs font-black">2hr avg</span>
                </div>
                <p className="text-[8px] font-bold text-neutral-600">Service Time</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

